import { ChannelGrouping, IndicationLevel } from '@/types/charts';
import { CubeQuery } from '@/types/cube';
import { Filter, LogicalAndFilter, TimeDimension } from '@cubejs-client/core';

export const HCOKeys = {
  all: ['hco'] as const,
  timeline: (...parts: readonly unknown[]) => [...HCOKeys.all, 'interaction-timeline', ...parts] as const,
  list: (query: CubeQuery) => [...HCOKeys.all, 'list', query] as const,
  totalCount: (query: CubeQuery) => [...HCOKeys.all, 'total-hco-count', query] as const,
  details: (hcoBk: string | undefined) => [...HCOKeys.all, 'details', hcoBk] as const,
  parentLink: (hcoBk: string | undefined) => [...HCOKeys.all, 'parent-link', hcoBk] as const,
  filteredHcpBks: (hcoBk: string | undefined, includeAllDepartments: boolean, globalFilters?: LogicalAndFilter) =>
    [...HCOKeys.all, 'filtered-hcp-bks', hcoBk, includeAllDepartments, globalFilters] as const,
  hcpCount: (hcoBk: string, includeAllDepartments: boolean, query: CubeQuery) =>
    [...HCOKeys.all, 'hcp-count', hcoBk, includeAllDepartments, query] as const,
  departmentCount: (hcoBk: string, includeAllDepartments: boolean, query: CubeQuery) =>
    [...HCOKeys.all, 'department-count', hcoBk, includeAllDepartments, query] as const,
  searchDetails: (hcoBks: string[]) => [...HCOKeys.all, 'search-details', ...hcoBks] as const,
  search: (phrase: string, page: string, size: string, exactMatch: boolean) =>
    [...HCOKeys.all, 'search', phrase, page, size, exactMatch] as const,
  emailConsentStatus: (hcoBk: string | undefined, includeAllDepartments: boolean, globalFilters?: LogicalAndFilter) =>
    [...HCOKeys.all, 'email-consent', 'status', hcoBk, includeAllDepartments, globalFilters] as const,
  hcpTypesDistribution: (hcoBk: string | undefined, includeAllDepartments: boolean, globalFilters?: LogicalAndFilter) =>
    [...HCOKeys.all, 'hcp-types-distribution', hcoBk, includeAllDepartments, globalFilters] as const,
  hcpSpecialtiesDistribution: (
    hcoBk: string | undefined,
    includeAllDepartments: boolean,
    globalFilters?: LogicalAndFilter,
  ) => [...HCOKeys.all, 'hcp-specialties-distribution', hcoBk, includeAllDepartments, globalFilters] as const,
  overallCoverageReached: (
    hcoBk: string,
    includeAllDepartments: boolean,
    dateRange: readonly string[],
    globalFilters?: LogicalAndFilter,
    localFilters?: Filter[],
  ) =>
    [
      ...HCOKeys.all,
      'overall-coverage-reached',
      hcoBk,
      includeAllDepartments,
      ...dateRange,
      globalFilters,
      localFilters,
    ] as const,
  overallCoverageTotal: (hcoBk: string, includeAllDepartments: boolean, globalFilters?: LogicalAndFilter) =>
    [...HCOKeys.all, 'overall-coverage-total', hcoBk, includeAllDepartments, globalFilters] as const,
  webPortalRegistration: (
    hcoBk: string | undefined,
    includeAllDepartments: boolean,
    globalFilters?: LogicalAndFilter,
  ) => [...HCOKeys.all, 'web-portal-registration', hcoBk, includeAllDepartments, globalFilters] as const,
  engagementFilterOptions: (hcoBk: string | undefined, includeAllDepartments: boolean, query: CubeQuery) =>
    [...HCOKeys.all, 'hco-engagement-filter-options', hcoBk, includeAllDepartments, query] as const,
  websiteInteractionIds: (
    hcoBk: string | undefined,
    includeAllDepartments: boolean,
    websiteNames: readonly string[],
    globalFilters: LogicalAndFilter,
    dateRange: TimeDimension,
  ) =>
    [
      ...HCOKeys.all,
      'hco-website-interaction-ids',
      hcoBk,
      includeAllDepartments,
      websiteNames,
      globalFilters,
      dateRange,
    ] as const,
  frequencyInteractionData: (
    hcoBk: string | undefined,
    includeAllDepartments: boolean,
    mode: string,
    query?: CubeQuery,
  ) => [...HCOKeys.all, 'hco-frequency-interaction', hcoBk, includeAllDepartments, mode, query] as const,
  frequencyPopupData: (
    hcoBk: string | undefined,
    includeAllDepartments: boolean,
    isAllHcps: boolean,
    query: CubeQuery,
  ) => [...HCOKeys.all, 'hco-frequency-popup', hcoBk, includeAllDepartments, isAllHcps, query] as const,
  engagementPerChannel: (
    hcoBk: string | undefined,
    includeAllDepartments: boolean,
    channelGrouping: ChannelGrouping,
    globalFilters?: LogicalAndFilter,
    localFilters?: Filter[],
  ) =>
    [
      ...HCOKeys.all,
      'hco-engagement-per-channel',
      hcoBk,
      includeAllDepartments,
      channelGrouping,
      globalFilters,
      ...(localFilters?.length ? [localFilters] : []),
    ] as const,
  engagementPerIndication: (
    hcoBk: string | undefined,
    includeAllDepartments: boolean,
    channelGrouping: ChannelGrouping,
    indicationLevel: IndicationLevel,
    globalFilters?: LogicalAndFilter,
    localFilters?: Filter[],
  ) =>
    [
      ...HCOKeys.all,
      'hco-engagement-per-indication',
      hcoBk,
      includeAllDepartments,
      channelGrouping,
      indicationLevel,
      globalFilters,
      ...(localFilters?.length ? [localFilters] : []),
    ] as const,
  engagementPerProduct: (
    hcoBk: string | undefined,
    includeAllDepartments: boolean,
    channelGrouping: ChannelGrouping,
    dateRange: readonly string[],
    globalFilters?: LogicalAndFilter,
    localFilters?: Filter[],
  ) =>
    [
      ...HCOKeys.all,
      'hco-engagement-per-product',
      hcoBk,
      includeAllDepartments,
      channelGrouping,
      ...dateRange,
      globalFilters,
      ...(localFilters?.length ? [localFilters] : []),
    ] as const,
  emailCampaignFunnel: (
    hcoBk: string | undefined,
    includeAllDepartments: boolean,
    dateRange: readonly string[],
    globalFilters?: LogicalAndFilter,
    localFilters?: Filter[],
  ) =>
    [
      ...HCOKeys.all,
      'hco-engagement-email-campaign-funnel',
      hcoBk,
      includeAllDepartments,
      dateRange,
      globalFilters,
      localFilters,
    ] as const,
  emailCampaignTable: (
    hcoBk: string | undefined,
    includeAllDepartments: boolean,
    dateRange: readonly string[],
    globalFilters?: LogicalAndFilter,
    localFilters?: Filter[],
  ) =>
    [
      ...HCOKeys.all,
      'hco-engagement-email-campaign-table',
      hcoBk,
      includeAllDepartments,
      dateRange,
      globalFilters,
      localFilters,
    ] as const,
  latestInteraction: (
    hcoBk: string,
    includeAllDepartments: boolean,
    globalFilters: LogicalAndFilter,
  ): readonly unknown[] => [
    ...HCOKeys.all,
    'latest-interaction',
    'interaction',
    hcoBk,
    includeAllDepartments,
    globalFilters,
  ],
  rocheCollaboratorsRanking: (
    hcoBk: string,
    includeAllDepartments: boolean,
    dateRange: readonly string[],
    globalFilters: LogicalAndFilter,
    localFilters?: Filter[],
  ): readonly unknown[] => [
    ...HCOKeys.all,
    'roche-collaborators',
    'ranking',
    hcoBk,
    includeAllDepartments,
    dateRange,
    globalFilters,
    localFilters,
  ],
  rocheCollaboratorsDetail: (
    hcoBk: string,
    includeAllDepartments: boolean,
    dateRange: readonly string[],
    globalFilters: LogicalAndFilter,
    employeePks: string[],
    localFilters?: Filter[],
  ): readonly unknown[] => [
    ...HCOKeys.all,
    'roche-collaborators',
    'detail',
    hcoBk,
    includeAllDepartments,
    dateRange,
    globalFilters,
    employeePks,
    localFilters,
  ],
};

export const HcoNewsKeys = {
  all: [...HCOKeys.all, 'news'] as const,
  list: (hcoName: string) => [...HcoNewsKeys.all, 'list', hcoName] as const,
};

export const HcoSegmentationMatrixKeys = {
  all: [...HCOKeys.all, 'segmentation-matrix'] as const,
  contacted: (hcoBk: string, query: CubeQuery) =>
    [...HcoSegmentationMatrixKeys.all, 'contacted', hcoBk, query] as const,
  total: (hcoBk: string, query: CubeQuery) => [...HcoSegmentationMatrixKeys.all, 'total', hcoBk, query] as const,
};

export const HcoLogoKeys = {
  all: [...HCOKeys.all, 'logo'] as const,
  detail: (hcoBk: string) => [...HcoLogoKeys.all, hcoBk] as const,
  batch: (hcoBks: string[]) => [...HcoLogoKeys.all, 'batch', [...hcoBks].sort().join(',')] as const,
};
